import { CartItemRow } from '@/components/creve/CartItemRow';
import { LandingFooter } from '@/components/creve/LandingFooter';
import { LandingHeader } from '@/components/creve/LandingHeader';
import { EmptyState, PageLayout } from '@/components/poof-ui';
import { Button } from '@/components/ui/button';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { CartItemsResponse, subscribeManyCartItems } from '@/lib/collections/cartItems';
import { useAuth } from '@pooflabs/web';
import { ShoppingBag, ShoppingCart } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

export const CartPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data } = useRealtimeData<CartItemsResponse[]>(
    subscribeManyCartItems,
    !!user?.address,
    user?.address ? `where owner is '${user.address}'` : ''
  );

  const items = data ?? [];
  const total = items.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 0), 0);

  return (
    <PageLayout fullBleed footer={<LandingFooter />}>
      <LandingHeader />
      <div className='container py-10 sm:py-14'>
        <div className='mx-auto max-w-3xl'>
          <div className='mb-8 flex items-center gap-3'>
            <span className='flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-primary'>
              <ShoppingCart className='h-5 w-5' />
            </span>
            <div>
              <h1 className='font-heading text-2xl font-bold text-foreground sm:text-3xl'>
                Your cart
              </h1>
              <p className='text-sm text-muted-foreground'>
                Review your items before checkout.
              </p>
            </div>
          </div>

          {!user ? (
            <div className='rounded-2xl border border-border bg-card p-10 text-center'>
              <p className='text-sm text-muted-foreground'>Log in to view your cart.</p>
            </div>
          ) : items.length === 0 ? (
            <div className='rounded-2xl border border-border bg-card'>
              <EmptyState
                icon={<ShoppingBag className='h-6 w-6' />}
                title='Your cart is empty'
                description='Items you add from vendor listings will show up here.'
                action={
                  <Button
                    onClick={() => navigate('/')}
                    className='rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90'
                  >
                    Browse vendors
                  </Button>
                }
              />
            </div>
          ) : (
            <>
              <div className='space-y-4'>
                {items.map((item) => (
                  <CartItemRow key={item.id} item={item} />
                ))}
              </div>

              <div className='mt-8 rounded-2xl border border-border bg-card p-6'>
                <div className='flex items-center justify-between text-base font-semibold text-foreground'>
                  <span>Total</span>
                  <span className='font-heading text-xl'>₦{total.toLocaleString('en-NG')}</span>
                </div>
                <Button
                  disabled
                  size='lg'
                  className='mt-5 w-full rounded-full bg-primary text-base text-primary-foreground disabled:opacity-60'
                >
                  Checkout — coming very soon
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default CartPage;
